// React
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Global Context
import { GlobalContext } from '../../../contexts/Global';

// Kendo
import { Button, Chip } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Field, FieldWrapper, Form, FormElement } from '@progress/kendo-react-form';
import { Input } from '@progress/kendo-react-inputs';
import { GridLayout, GridLayoutItem } from '@progress/kendo-react-layout';
import { Label } from '@progress/kendo-react-labels';

// Components
import Error from '../../../components/Page/Error';
import PageField from '../../../components/Page/Field';
import Success from '../../../components/Page/Success';
import Title from '../../../components/Page/Title';
import Loader from '../../../components/Loader/Loader';

const requiredValidator = (value) => value && value.trim() ? '' : 'Campo obbligatorio';
const emailValidator = (value) => {
    if (!value || !value.trim()) {
        return 'Campo obbligatorio';
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Indirizzo email non valido';
};

const formatDate = (value) => value ? new Date(value).toLocaleString('it-IT') : '-';

const UserPage = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const { user: currentUser, setUser: setCurrentUser } = useContext(GlobalContext);
    const [ viewedUser, setViewedUser ] = useState(null);
    const [ response, setResponse ] = useState(null);
    const [ isLoading, showLoader ] = useState(false);
    const [ disableConfirmation, showDisableConfirmation ] = useState(false);

    useEffect(() => {
        showLoader(true);
        fetch(process.env.REACT_APP_API_BASE_URL + '/admin/user/' + userId, {
            headers: {
                Authorization: 'Bearer ' + currentUser.access_token
            }
        })
            .then((response) => response.json())
            .then((response) => {
                if (response.status === 'success') {
                    setViewedUser(response.data.user);
                }
                else {
                    setResponse(response);
                }
            })
            .catch((error) => setResponse({ status: 'error', message: error.message }))
            .finally(() => showLoader(false));
    }, [ currentUser, userId ]);

    const updateUser = (data) => {
        const emailChanged = data.email.trim().toLowerCase() !== viewedUser.email;
        showLoader(true);
        fetch(process.env.REACT_APP_API_BASE_URL + '/admin/user/' + viewedUser.id, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + currentUser.access_token
            },
            body: JSON.stringify({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email
            })
        })
            .then((response) => response.json())
            .then((response) => {
                setResponse(response);
                if (response.status === 'success') {
                    setViewedUser(response.data.user);

                    if (viewedUser.id === currentUser.id && emailChanged) {
                        localStorage.removeItem('user');
                        setCurrentUser(null);
                        navigate('/');
                    }
                }
            })
            .catch((error) => setResponse({ status: 'error', message: error.message }))
            .finally(() => showLoader(false));
    };

    const updateStatus = (status) => {
        showLoader(true);
        fetch(process.env.REACT_APP_API_BASE_URL + '/admin/user/' + viewedUser.id + '/status', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + currentUser.access_token
            },
            body: JSON.stringify({ status })
        })
            .then((response) => response.json())
            .then((response) => {
                setResponse(response);
                if (response.status === 'success') {
                    setViewedUser(response.data.user);
                }
            })
            .catch((error) => setResponse({ status: 'error', message: error.message }))
            .finally(() => {
                showDisableConfirmation(false);
                showLoader(false);
            });
    };

    const status = {
        active: { label: 'Attivo', themeColor: 'success' },
        pending: { label: 'In attesa', themeColor: 'warning' },
        disabled: { label: 'Disabilitato', themeColor: 'error' }
    }[viewedUser?.status] || { label: '-', themeColor: 'base' };

    return (
        <>
            <Title>Dettaglio utente</Title>

            { viewedUser && (
                <div className="section">
                    <Form
                        key={ viewedUser.updated_at }
                        initialValues={{
                            first_name: viewedUser.first_name,
                            last_name: viewedUser.last_name,
                            email: viewedUser.email
                        }}
                        onSubmit={ updateUser }
                        render={(formRenderProps) => (
                            <FormElement>
                                <GridLayout
                                    cols={[ { width: '1fr' }, { width: '1fr' }, { width: '1fr' } ]}
                                    gap={{ rows: 20, cols: 20 }}
                                >
                                    <GridLayoutItem>
                                        <FieldWrapper>
                                            <Label className="k-form-label">Nome</Label>
                                            <Field name="first_name" component={ Input } validator={ requiredValidator } />
                                        </FieldWrapper>
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <FieldWrapper>
                                            <Label className="k-form-label">Cognome</Label>
                                            <Field name="last_name" component={ Input } validator={ requiredValidator } />
                                        </FieldWrapper>
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <FieldWrapper>
                                            <Label className="k-form-label">Email</Label>
                                            <Field name="email" component={ Input } validator={ emailValidator } />
                                        </FieldWrapper>
                                    </GridLayoutItem>

                                    <GridLayoutItem>
                                        <PageField label="ID" type="text" value={ viewedUser.id } />
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <PageField label="Stato">
                                            <Chip text={ status.label } themeColor={ status.themeColor } rounded={ null } />
                                        </PageField>
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <PageField label="Amministratore" type="text" value={ viewedUser.is_admin ? 'Sì' : 'No' } />
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <PageField label="Segnalazioni" type="text" value={ viewedUser.issues_count } />
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <PageField label="Attivato il" type="text" value={ formatDate(viewedUser.activated_at) } />
                                    </GridLayoutItem>
                                    <GridLayoutItem>
                                        <PageField label="Creato il" type="text" value={ formatDate(viewedUser.created_at) } />
                                    </GridLayoutItem>

                                    <GridLayoutItem colSpan={ 3 }>
                                        <div className="k-form-buttons k-justify-content-between k-flex-wrap">
                                            <div>
                                                <Button
                                                    type="submit"
                                                    rounded={ null }
                                                    size="large"
                                                    themeColor="primary"
                                                    disabled={ !formRenderProps.modified || !formRenderProps.valid }
                                                >
                                                    SALVA
                                                </Button>
                                                <Button rounded={ null } size="large" onClick={ () => navigate('/users') }>
                                                    TORNA ALLA LISTA
                                                </Button>
                                            </div>

                                            { !viewedUser.is_admin && viewedUser.status === 'active' && (
                                                <Button
                                                    rounded={ null }
                                                    size="large"
                                                    themeColor="error"
                                                    onClick={ () => showDisableConfirmation(true) }
                                                >
                                                    DISABILITA
                                                </Button>
                                            )}

                                            { !viewedUser.is_admin && viewedUser.status === 'disabled' && (
                                                <Button
                                                    rounded={ null }
                                                    size="large"
                                                    themeColor="success"
                                                    onClick={ () => updateStatus('active') }
                                                >
                                                    RIABILITA
                                                </Button>
                                            )}
                                        </div>
                                    </GridLayoutItem>
                                </GridLayout>
                            </FormElement>
                        )}
                    />
                </div>
            )}

            { disableConfirmation && (
                <Dialog onClose={ () => showDisableConfirmation(false) }>
                    <p style={{ margin: '25px', textAlign: 'center' }}>
                        Vuoi disabilitare { viewedUser.full_name }?
                        <br />
                        L'utente non potrà più accedere o usare le sessioni esistenti.
                    </p>
                    <DialogActionsBar>
                        <Button rounded={ null } size="large" themeColor="primary" onClick={ () => showDisableConfirmation(false) }>
                            ANNULLA
                        </Button>
                        <Button rounded={ null } size="large" themeColor="error" onClick={ () => updateStatus('disabled') }>
                            DISABILITA
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <Success enter={ response } exit={ setResponse } />
            <Error enter={ response } exit={ setResponse } />
            { isLoading && (<Loader />) }
        </>
    );
};

export default UserPage;
